import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * Spec 032 US5 — Optional encrypted EEO (demographic) blob.
 *
 * Stored as a single AES-256-encrypted JSON blob on `ApplicationProfileEeo`.
 * The wire shape mirrors the four ATS demographic questions we expect:
 *
 *   - gender             — most ATSes: male / female / non_binary / decline
 *   - race               — multi-category enum from EEO-1
 *   - veteranStatus      — protected_veteran / not_a_veteran / decline
 *   - disabilityStatus   — yes / no / decline_to_self_identify
 *
 * Every field is **optional**: the user may fill some, all, or none of
 * them. An empty payload is equivalent to "no EEO row" — workers must
 * fall back to the "Decline to self-identify" option on the form.
 */
export const updateEeoSchema = z.object({
  gender: z.string().trim().max(50).optional().nullable(),
  race: z.string().trim().max(100).optional().nullable(),
  veteranStatus: z.string().trim().max(50).optional().nullable(),
  disabilityStatus: z.string().trim().max(50).optional().nullable(),
});

export class UpdateEeoDto extends createZodDto(updateEeoSchema) {}

/** Decrypted EEO payload shape — produced by `ApplicationProfileEncryption.decryptEeo`
 *  and returned to the client as the `GET /me/application-profile/eeo` body
 *  (a non-existent row is surfaced as `null`, not an empty object, so the
 *  client can render the "no EEO information provided" empty state cleanly). */
export type EeoResponse = z.infer<typeof updateEeoSchema>;
